import { type FC } from "react";

import { type AnomalyFlag } from "./observabilityModel";

const FLAG_LABELS: Record<AnomalyFlag, string> = {
  cache_boundary_moved: "marker",
  cache_cold: "cold",
  cache_hit_drop: "hit↓",
  cache_miss: "miss",
  error: "error",
  long: "slow",
  multi_step: "multi",
};

const FLAG_COLORS: Record<AnomalyFlag, string> = {
  cache_boundary_moved:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  // Cold = expected empty cache (first wake or post-TTL gap). Neutral sky tint
  // Distinguishes it from the alarming rose of an unexpected miss.
  cache_cold: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
  cache_hit_drop:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  cache_miss:
    "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  error: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
  long: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
  multi_step: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
};

const FLAG_TITLES: Record<AnomalyFlag, string> = {
  cache_boundary_moved:
    "Cache marker boundary slid forward — new messages picked up the marker. Healthy when hit rate stays high; concerning only alongside a hit drop.",
  cache_cold:
    "No cache read on this wake — first wake or >55m since prior. Expected under 1h TTL; one-time write cost.",
  cache_hit_drop: "Hit rate dropped >20% vs prior wake.",
  cache_miss:
    "Zero cache reads, prior wake within TTL window — unexpected. Suggests prefix drift, model rollover, or a real bug.",
  error: "Generation finished with error.",
  long: "Wake duration exceeded 30s.",
  multi_step: "More than one LLM step in this wake.",
};

export const AnomalyBadge: FC<{ flag: AnomalyFlag }> = ({ flag }) => (
  <span
    className={`rounded-sm px-1 py-px font-mono text-[9px] uppercase tracking-wider ${FLAG_COLORS[flag]}`}
    title={FLAG_TITLES[flag]}
  >
    {FLAG_LABELS[flag]}
  </span>
);

export const ErrorKindBadge: FC<{ label: string }> = ({ label }) => (
  <span
    className="rounded-sm bg-rose-100 px-1 py-px font-mono text-[9px] uppercase tracking-wider text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
    title={`Wake errored: ${label}`}
  >
    {label.replace(/_/g, " ")}
  </span>
);
